import React, { useContext } from 'react';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { SiteContext } from '../../../context/SiteContext';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { setCountryCode } from '../../common/HelperMethods';
import { PageAbsolutePath } from '../../../consts/Common';
import {
  InsuranceJourneyContext,
  PaymentMethod
} from '../../common/CommonTypes';
import editImage from '../../../images/pencil.svg';
import PaymentSummary from './PaymentSummary';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { PagesWithDropdown } from '../../common/Constants';

type SummaryItemProps = {
  label: string;
  value: string | React.ReactNode;
  redirect: string;
};

const SummaryItem = ({
  label,
  value,
  redirect
}: SummaryItemProps): JSX.Element => {
  return (
    <div className='flex-row m2-bottom'>
      <div className='flex-column'>{label}</div>
      <div className='flex-column'>{value}</div>
      <div className='flex-column flex-no-grow'>
        <Link to={redirect}>
          <img src={editImage} alt='Edit button' width='28px' />
        </Link>
      </div>
    </div>
  );
};

const CheckYourDetailsPage = (): JSX.Element => {
  const { site } = useContext(SiteContext);
  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const {
    personalDetails,
    drivingHistoryDetails,
    vehicleInfo,
    coverDetails,
    addressDetails,
    additionalDriverDetails,
    paymentDetails,
    staticData,
    savingQuote
  } = journey;

  const { commonEvents } = pageEvents;
  const { setSavingQuote, onPageChange } = commonEvents;

  const navigate = useNavigate();

  const onBackClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.OptionalCover,
      site.countryCode
    );
    navigate(path);
  };

  const onBuyClick = () => {
    let path = '';
    if (paymentDetails.selectedPaymentMethod === PaymentMethod.annually) {
      path = setCountryCode(
        PageAbsolutePath.Insurance.PaymentPage,
        site.countryCode
      );
    } else {
      path = setCountryCode(
        PageAbsolutePath.Insurance.AboutTermsOfBusinessAndFairUsageNotice,
        site.countryCode
      );
    }
    navigate(path);
    onPageChange(PageAbsolutePath.Insurance.YourQuote, true);
  };

  const getDateString = (date?: Date) => {
    if (date === undefined) return '';
    if (CommonValidation.isDateNull(date, false)) return '';
    if (CommonValidation.isEmpty(date)) return '';
    if (!CommonValidation.isValidDate(date, false)) return '';

    return new Date(date).toLocaleDateString('en-GB');
  };

  const getDropDownText = (
    page: PagesWithDropdown,
    dropDownName: string,
    value: string
  ): string => {
    const dropDown = staticData.find(
      (d) => d.page === page && d.controlName === dropDownName
    );
    const dropDownValue =
      dropDown?.values?.find((g) => g.value === value)?.text ?? '';

    return dropDownValue;
  };

  const personalDetailsSection: React.ReactNode = (
    <div>
      <span className='bold m1-right'>Name:</span>
      <span>{`${personalDetails.title} ${personalDetails.firstName}  ${personalDetails.lastName}`}</span>
      <br />
      <span className='bold m1-right'>Gender:</span>
      <span>
        {getDropDownText(
          PagesWithDropdown.AboutYouPage,
          'genders',
          personalDetails.gender
        )}
      </span>
      <br />
      <span className='bold m1-right'>DoB:</span>
      <span>{getDateString(personalDetails.dateOfBirth)}</span>
      <br />
      <span className='bold m1-right'>Marital status:</span>
      <span>
        {getDropDownText(
          PagesWithDropdown.AboutYouPage,
          'maritalStatus',
          personalDetails.maritalStatus
        )}
      </span>
      <br />
      <span className='bold m1-right'>UK residence:</span>
      <span>
        {personalDetails.residentSinceBirth === true
          ? getDateString(personalDetails.dateOfBirth)
          : getDateString(personalDetails.residentSinceDate)}
      </span>
      <br />
      <span className='bold m1-right'>Home owner:</span>
      <span>{personalDetails.ownHome === true ? 'Yes' : 'No'}</span>
    </div>
  );

  const contactDetailsSection: React.ReactNode = (
    <div>
      <span className='bold m1-right'>Home:</span>
      <span>{personalDetails.homePhone}</span>
      <br />
      <span className='bold m1-right'>Mobile:</span>
      <span>{personalDetails.mobilePhone}</span>
      <br />
      <span className='bold m1-right'>Email:</span>
      <span>{personalDetails.email}</span>
    </div>
  );

  const employmentSection: React.ReactNode = (
    <div>
      <span className='bold m1-right'>Occupation:</span>
      <span>{personalDetails.occupation}</span>
      <br />
      <span className='bold m1-right'>Business:</span>
      <span>{personalDetails.industry}</span>
      <br />
      <span className='bold m1-right'>Occupation type:</span>
      <span>
        {getDropDownText(
          PagesWithDropdown.AboutYouPage,
          'employmentStatus',
          personalDetails.employmentStatus
        )}
      </span>
    </div>
  );

  const licenceSection: React.ReactNode = (
    <div>
      <span className='bold m1-right'>Type:</span>
      <span>
        {getDropDownText(
          PagesWithDropdown.DrivingHistoryPage,
          'licence-types',
          drivingHistoryDetails.licenceType
        )}
      </span>
      <br />
      <span className='bold m1-right'>Held for:</span>
      <span>
        {getDropDownText(
          PagesWithDropdown.DrivingHistoryPage,
          'years-licence-held',
          drivingHistoryDetails.yearsLicenceHeld
        )}
      </span>
    </div>
  );

  const vehicleUseSection: React.ReactNode = (
    <div>
      <span className='bold m1-right'>Used for:</span>
      <span>
        {getDropDownText(
          PagesWithDropdown.InsuranceCoverDetailsPage,
          'car-uses',
          coverDetails.carUses
        )}
      </span>
      <br />
      <span className='bold m1-right'>Estimated annual mileage:</span>
      <span>
        {getDropDownText(
          PagesWithDropdown.InsuranceCoverDetailsPage,
          'estimated-mileages',
          coverDetails.estimatedMileage
        )}
      </span>
    </div>
  );

  const getDrivers = (): string => {
    let drivers = `${personalDetails.title} ${personalDetails.firstName}  ${personalDetails.lastName} `;

    if (additionalDriverDetails.whoWillDrive === 'INS') {
      drivers = drivers + 'only';
    } else {
      drivers =
        drivers +
        `and ${additionalDriverDetails.title} ${additionalDriverDetails.firstName} ${additionalDriverDetails.lastName}`;
    }

    return drivers;
  };

  const datesSection: React.ReactNode = (
    <div>
      <span className='bold m1-right'>Purchased:</span>
      <span>{getDateString(vehicleInfo.keysPickupDate)}</span>
      <br />
      <span className='bold m1-right'>Manufactured:</span>
      <span>
        {getDropDownText(
          PagesWithDropdown.InsuranceVehicleDetailsPage,
          'year-of-manufacture',
          vehicleInfo.yearOfManufacture
        )}
      </span>
    </div>
  );

  const vehicleLocationSection: React.ReactNode = (
    <div>
      <span className='bold m1-right'>Property:</span>
      <br />
      <span>
        {addressDetails.overnightParkedAddress.addressLine1 && (
          <>
            {addressDetails.overnightParkedAddress.addressLine1}
            <br />
          </>
        )}
        {addressDetails.overnightParkedAddress.addressLine2 && (
          <>
            {addressDetails.overnightParkedAddress.addressLine2}
            <br />
          </>
        )}
        {addressDetails.overnightParkedAddress.townOrCity && (
          <>
            {addressDetails.overnightParkedAddress.townOrCity}
            <br />
          </>
        )}
        {addressDetails.overnightParkedAddress.region && (
          <>
            {addressDetails.overnightParkedAddress.region}
            <br />
          </>
        )}
        {addressDetails.overnightParkedAddress.postCode && (
          <>{addressDetails.overnightParkedAddress.postCode}</>
        )}
      </span>
      <br />
      <span className='bold m1-right'>Overnight location:</span>
      <span>
        {getDropDownText(
          PagesWithDropdown.AddressDetailsPage,
          'overnight-parking-locations',
          addressDetails.locationVehicleParkedOvernight
        )}
      </span>
    </div>
  );

  const getClaimsAccidentsAndLosses = (): string | React.ReactNode => {
    if (drivingHistoryDetails.hadClaimAccidentLosses === true) {
      return (
        <div>
          {drivingHistoryDetails.claims.map((claim, index) => (
            <div key={`claim-${index}`}>
              <span className='bold m1-right'>Claim type:</span>
              <span>
                {getDropDownText(
                  PagesWithDropdown.DrivingHistoryPage,
                  'claim-types',
                  claim.claimType
                )}
              </span>
              <br />
              <span className='bold m1-right'>Claim date:</span>
              <span>{getDateString(claim.claimDate)}</span>
              {index !== drivingHistoryDetails.claims.length - 1 && <hr />}
            </div>
          ))}
        </div>
      );
    } else {
      return 'No';
    }
  };

  const getDrivingConvictions = (): string | React.ReactNode => {
    if (drivingHistoryDetails.hadDrivingConvictions === true) {
      return (
        <div>
          {drivingHistoryDetails.drivingConvictions.map(
            (convictions, index) => (
              <div key={`conviction-${index}`}>
                <span className='bold m1-right'>Conviction type:</span>
                <span>
                  {getDropDownText(
                    PagesWithDropdown.DrivingHistoryPage,
                    'driving-conviction-types',
                    convictions.convictionType
                  )}
                </span>
                <br />
                <span className='bold m1-right'>Conviction date:</span>
                <span>{getDateString(convictions.convictionDate)}</span>
                <br />
                <span className='bold m1-right'>Length of ban:</span>
                <span>
                  {getDropDownText(
                    PagesWithDropdown.DrivingHistoryPage,
                    'driving-ban-length-types',
                    convictions.lengthOfBan
                  )}
                </span>
                <br />
                <span className='bold m1-right'>Points:</span>
                <span>{convictions.points}</span>
                {index !==
                  drivingHistoryDetails.drivingConvictions.length - 1 && <hr />}
              </div>
            )
          )}
        </div>
      );
    } else {
      return 'No';
    }
  };

  const getMedicalConditions = (): string | React.ReactNode => {
    if (drivingHistoryDetails.hadMedicalConditionOrDisabilities === true) {
      return (
        <div>
          {drivingHistoryDetails.medicalConditions.map((condition, index) => (
            <div key={`condition-${index}`}>
              <span className='bold m1-right'>Type of condition:</span>
              <span>
                {getDropDownText(
                  PagesWithDropdown.DrivingHistoryPage,
                  'medical-conditions',
                  condition.typeOfCondition
                )}
              </span>
              <br />
              <span className='bold m1-right'>DVLA notified:</span>
              <span>{condition.hasDVLANotified}</span>
              <br />
              <span className='bold m1-right'>Can keep licence:</span>
              <span>{condition.canKeepLicence}</span>
              {index !== drivingHistoryDetails.medicalConditions.length - 1 && (
                <hr />
              )}
            </div>
          ))}
        </div>
      );
    } else {
      return 'No';
    }
  };

  const getVehicleModifications = (): string | React.ReactNode => {
    if (vehicleInfo.hasBeenModified === true) {
      return (
        <div>
          {vehicleInfo.modifications.map((modification, index) => (
            <div key={`modification-${index}`}>
              <span className='bold m1-right'>Modification type:</span>
              <span>
                {getDropDownText(
                  PagesWithDropdown.InsuranceVehicleDetailsPage,
                  'modification-types',
                  modification.modificationType
                )}
              </span>
              <br />
              <span className='bold m1-right'>For disability:</span>
              <span>{modification.forDisability}</span>
              {index !== vehicleInfo.modifications.length - 1 && <hr />}
            </div>
          ))}
        </div>
      );
    } else {
      return 'No';
    }
  };

  return (
    <div>
      <PaymentSummary
        headerText='What you are buying'
        disableTabs={false}
        salesTelephoneNumber={
          site.brand.brandPhoneNumbers.quotesAndRenewalsNumber
        }
        hideMonthlyIfZero={true}
      />
      <h4 className='m1-top'>Check your details</h4>
      <p>
        Before you buy, make sure that all the details we have for you are
        correct.
      </p>
      <h4 className='m1-top'>About you</h4>
      <hr />
      <SummaryItem
        label='Personal details'
        value={personalDetailsSection}
        redirect={PageAbsolutePath.Insurance.AboutYou}
      />
      <SummaryItem
        label='Contact details'
        value={contactDetailsSection}
        redirect={PageAbsolutePath.Insurance.AboutYou}
      />
      <SummaryItem
        label='Existing insurance policy expiry month'
        value={personalDetails.expiryMonth}
        redirect={PageAbsolutePath.Insurance.AboutYou}
      />
      <SummaryItem
        label='New cover start date'
        value={`${getDateString(coverDetails.coverStartDate)} ${coverDetails.coverStartTime}`}
        redirect={PageAbsolutePath.Insurance.InsuranceCoverDetailsPage}
      />
      <SummaryItem
        label='Employment'
        value={employmentSection}
        redirect={PageAbsolutePath.Insurance.AboutYou}
      />
      <SummaryItem
        label='Licence'
        value={licenceSection}
        redirect={PageAbsolutePath.Insurance.DrivingHistory}
      />
      <SummaryItem
        label='Vehicle use'
        value={vehicleUseSection}
        redirect={PageAbsolutePath.Insurance.InsuranceCoverDetailsPage}
      />
      <SummaryItem
        label='Drivers'
        value={getDrivers()}
        redirect={PageAbsolutePath.Insurance.AdditionalDriverDetailsPage}
      />
      <SummaryItem
        label='Registered keeper'
        value={getDropDownText(
          PagesWithDropdown.InsuranceCoverDetailsPage,
          'vehicle-owners',
          coverDetails.registeredVehicleKeeper
        )}
        redirect={PageAbsolutePath.Insurance.InsuranceCoverDetailsPage}
      />
      <SummaryItem
        label='Vehicle owner'
        value={getDropDownText(
          PagesWithDropdown.InsuranceCoverDetailsPage,
          'vehicle-owners',
          coverDetails.registeredVehicleOwner
        )}
        redirect={PageAbsolutePath.Insurance.InsuranceCoverDetailsPage}
      />
      <SummaryItem
        label='No claims discount'
        value={getDropDownText(
          PagesWithDropdown.DrivingHistoryPage,
          'no-claim-years',
          drivingHistoryDetails.noClaimsBonusYear
        )}
        redirect={PageAbsolutePath.Insurance.DrivingHistory}
      />
      <SummaryItem
        label='Claims, accidents or losses in the last three years'
        value={getClaimsAccidentsAndLosses()}
        redirect={PageAbsolutePath.Insurance.DrivingHistory}
      />
      <SummaryItem
        label='Driving convictions in the last five years'
        value={getDrivingConvictions()}
        redirect={PageAbsolutePath.Insurance.DrivingHistory}
      />
      <SummaryItem
        label='Medical conditions'
        value={getMedicalConditions()}
        redirect={PageAbsolutePath.Insurance.DrivingHistory}
      />
      <SummaryItem
        label='Have you ever had an insurance policy declined, cancelled, voided or had special terms imposed?'
        value={
          drivingHistoryDetails.hadDeclinedInsurance === true ? 'Yes' : 'No'
        }
        redirect={PageAbsolutePath.Insurance.DrivingHistory}
      />

      <h4 className='m1-top'>About your car</h4>
      <hr />
      <SummaryItem
        label='Registration'
        value={vehicleInfo?.vehicle?.vrn || ''}
        redirect={PageAbsolutePath.Insurance.VehicleDetails}
      />
      <SummaryItem
        label='Bought from'
        value={getDropDownText(
          PagesWithDropdown.InsuranceVehicleDetailsPage,
          'dealers',
          vehicleInfo.dealer
        )}
        redirect={PageAbsolutePath.Insurance.VehicleDetails}
      />
      <SummaryItem
        label='Current value'
        value={vehicleInfo.carValue}
        redirect={PageAbsolutePath.Insurance.VehicleDetails}
      />
      <SummaryItem
        label='Condition when bought'
        value={getDropDownText(
          PagesWithDropdown.InsuranceVehicleDetailsPage,
          'new-used',
          vehicleInfo.isItNewOrUsed
        )}
        redirect={PageAbsolutePath.Insurance.VehicleDetails}
      />
      <SummaryItem
        label='Vehicle modifications'
        value={getVehicleModifications()}
        redirect={PageAbsolutePath.Insurance.VehicleDetails}
      />
      <SummaryItem
        label='Dates'
        value={datesSection}
        redirect={PageAbsolutePath.Insurance.VehicleDetails}
      />
      <SummaryItem
        label='Tracking device'
        value={
          vehicleInfo.hasTrackingDevice === true
            ? vehicleInfo.trackingDevice
            : 'No'
        }
        redirect={PageAbsolutePath.Insurance.VehicleDetails}
      />
      <SummaryItem
        label='Vehicle location'
        value={vehicleLocationSection}
        redirect={PageAbsolutePath.Insurance.AddressDetailsPage}
      />
      <SummaryItem
        label='Number of cars at your household'
        value={getDropDownText(
          PagesWithDropdown.AddressDetailsPage,
          'no-of-cars',
          addressDetails.numberOfCarsAtHousehold
        )}
        redirect={PageAbsolutePath.Insurance.AddressDetailsPage}
      />

      <div className='even-space-out flex-row form-nav'>
        <ButtonBack
          name='btn-back'
          text='Back'
          onClick={onBackClick}
          additionalClassNames='btn-back'
        />
        <ButtonContinue
          name='btn-save-later'
          text='Save For Later'
          onClick={() => setSavingQuote(true)}
          additionalClassNames='btn-save'
          disabled={savingQuote}
        />
        <ButtonContinue
          name='btn-buy'
          text='Buy'
          onClick={onBuyClick}
          additionalClassNames='btn-continue'
        />
      </div>
    </div>
  );
};

export default CheckYourDetailsPage;
